<template>
  <div class="author-wrap">
    <header class="author-header">
      <span class="author-title"
        >Kingdee Cloud Building
        <span class="author-subtitle">Art Space</span></span
      >
      <span class="exhibit-menu" @click="navToExhibitList">查看更多</span>
    </header>
    <div class="author-swiper" v-if="carouselList && carouselList.length > 0">
      <swiper
        :modules="modules"
        :slides-per-view="1"
        :space-between="0"
        :pagination="{ clickable: true }"
        class="author-swiper-container"
      >
        <swiper-slide v-for="(item, index) in carouselList" :key="index">
          <div class="swiper-media-wrapper">
            <img
              :src="getImageUrl(item)"
              :alt="`Carousel ${index + 1}`"
              class="swiper-media"
            />
          </div>
        </swiper-slide>
      </swiper>
    </div>
    <section class="author-info">
      <div class="author-divider"></div>
      <div class="author-title-row">
        <span class="author-name"
          >{{ artistDetail.name_cn }}
          <span class="en">{{ artistDetail.name_en }}</span></span
        >
        <div class="author-meta">
          <div class="author-birth" v-if="birthYear">b. {{ birthYear }}</div>
          <div class="author-location">
            {{ artistDetail.sign_cn }}<br />
            <span class="en">{{ artistDetail.sign_en }}</span>
          </div>
        </div>
      </div>
      <div class="author-avatar-wrap">
        <img class="author-avatar" :src="artistDetail.avatar" />
      </div>
      <div class="author-desc">
        {{ artistDetail.intro_cn }}
      </div>
      <div class="author-desc">
        {{ artistDetail.intro_en }}
      </div>
    </section>
    <Footer
      :is-liked="isLiked"
      :like-count="artistDetail.likeCount"
      :share-count="artistDetail.shareCount"
      @onLike="handleLike"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import Footer from "@/components/Footer.vue";
import {
  getArtistDetail,
  recordVisit,
  recordShare,
  getLikeStatus,
  doLike,
  doUnlike,
} from "@/service/api";
import { useRoute } from "vue-router";
import router from "@/router";
import dayjs from "dayjs";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useWxShare, wxAuth } from "@/hooks/useWechatAuth";
import { showToast, showFailToast } from "vant";

const modules = [Pagination];

const artistDetail = ref({});

const birthYear = computed(() => {
  return artistDetail.value.birthday
    ? dayjs(artistDetail.value.birthday).format("YYYY")
    : "";
});

// carousel 图片列表
const carouselList = computed(() => {
  return artistDetail.value.carousel || [];
});

// 获取图片URL
const getImageUrl = (item) => {
  if (typeof item === "string") {
    return item;
  }
  if (item && item.url) {
    return item.url;
  }
  return item;
};

const route = useRoute();

const navToExhibitList = () => {
  router.push("/exhibitList");
};

const isLiked = ref(false);

const getLikeInfo = () => {
  getLikeStatus({
    targetId: route.query.id,
    targetType: "artist",
  }).then((res) => {
    isLiked.value = res.liked;
  });
};

onMounted(async () => {
  await wxAuth();
  const res = await getArtistDetail(route.query.id);
  artistDetail.value = res.artist || {};

  recordVisit("artist", route.query.id);

  getLikeInfo();

  handleShare();
});

const handleLike = (flag) => {
  (flag ? doLike : doUnlike)({
    targetId: route.query.id,
    targetType: "artist",
  })
    .then(() => {
      getLikeInfo();
      flag ? artistDetail.value.likeCount++ : artistDetail.value.likeCount--;
    })
    .catch((err) => {
      showFailToast(err);
    });
};

const handleShare = () => {
  useWxShare({
    title: "金蝶艺术馆",
    desc: artistDetail.value.name_cn,
    link: `${window.location.origin}${window.location.pathname}/#/author?id=${route.query.id}`,
    imgUrl: artistDetail.value.avatar,
    success: async () => {
      const res = await recordShare("artist", route.query.id);
      if (res.message === "ok") {
        artistDetail.value.shareCount++;
      }
    },
  });
};
</script>

<style scoped lang="scss">
.author-wrap {
  background: #fff;
  min-height: 100vh;
  font-family: "PingFang SC", "Helvetica Neue", Arial, sans-serif;
  color: #222;
  padding: 40px 24px 90px;
}

.author-header {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  font-size: 12px;
  background: #fff;

  .author-title {
    font-weight: 500;

    .author-subtitle {
      color: #bdbdbd;
      margin-left: 4px;
    }
  }

  .author-menu {
    font-size: 12px;
    color: #222;
    letter-spacing: 2px;
  }
}

.author-swiper {
  width: 100%;
  margin: 17px 0 0;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  .author-swiper-container {
    width: 100%;
    border-radius: 6px;
  }

  .swiper-media-wrapper {
    width: 100%;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
  }

  .swiper-media {
    width: 100%;
    height: 200px;
    display: block;
  }

  // Swiper 分页器样式
  :deep(.swiper-pagination) {
    bottom: 10px;

    .swiper-pagination-bullet {
      width: 6px;
      height: 6px;
      background: rgba(255, 255, 255, 0.8);
      opacity: 0.5;

      &.swiper-pagination-bullet-active {
        opacity: 1;
        background: #fff;
      }
    }
  }
}

.author-info {
  margin: 45px auto 0 auto;

  .author-title-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 16px;

    .author-name {
      font-size: 28px;
      font-weight: bold;
      letter-spacing: 1px;
    }

    .author-meta {
      text-align: right;
      color: #888;
      font-size: 18px;

      .author-birth {
        margin-bottom: 4px;
      }

      .author-location {
        font-size: 16px;

        .en {
          color: #aaa;
          font-size: 15px;
        }
      }
    }
  }

  .author-divider {
    border-bottom: 3px solid #222;
    margin-bottom: 32px;
  }

  .author-avatar-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 32px;

    .author-avatar {
      width: 220px;
      height: 220px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid #222;
      background: #eee;
    }
  }

  .author-desc {
    font-size: 17px;
    color: #222;
    line-height: 2;
    margin-bottom: 24px;
    text-align: justify;
    white-space: pre-line;
  }
}
</style>
